// src/components/elevation/GroundElevationCard.js
// Read-only ground elevation (TessaDEM) for one asset — kept apart from the register's surveyed value.
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, HStack, VStack, Text, Badge, IconButton, Tooltip, Skeleton, SimpleGrid, useColorModeValue,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import { fetchAssetElevation, refreshAssetElevation } from '../../services/elevationService';

const fmt = (v, d = 2) => (v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(d));

const Stat = ({ label, value, unit }) => (
  <Box>
    <Text fontSize="2xs" textTransform="uppercase" letterSpacing="wide" color="gray.500">{label}</Text>
    <Text fontSize="sm" fontWeight="700">{value}{value !== '—' && unit ? <Text as="span" fontSize="xs" fontWeight="500"> {unit}</Text> : null}</Text>
  </Box>
);
Stat.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string.isRequired, unit: PropTypes.string };

const GroundElevationCard = ({ assetId, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const bg = useColorModeValue('teal.50', 'rgba(56,178,172,0.08)');
  const border = useColorModeValue('teal.100', 'rgba(56,178,172,0.25)');

  useEffect(() => {
    if (!assetId) return undefined;
    const ctrl = new AbortController();
    setLoading(true); setError(null);
    fetchAssetElevation(assetId, { signal: ctrl.signal })
      .then(setData)
      .catch((e) => { if (e?.name !== 'CanceledError' && e?.name !== 'AbortError') setError(e?.response?.data?.message || e.message); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [assetId]);

  const refresh = useCallback(async () => {
    setBusy(true); setError(null);
    try { setData(await refreshAssetElevation(assetId)); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  }, [assetId]);

  if (!assetId) return null;
  const t = data?.terrain;
  const p = data?.profile;
  return (
    <Box p={3} bg={bg} borderWidth="1px" borderColor={border} borderRadius="lg">
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="700">Ground elevation</Text>
          <Badge colorScheme="teal" variant="subtle" fontSize="2xs">TessaDEM</Badge>
        </HStack>
        <Tooltip label="Re-fetch from the asset's GPS" hasArrow>
          <IconButton size="xs" variant="ghost" aria-label="Refresh ground elevation" icon={<RepeatIcon />} isLoading={busy} onClick={refresh} />
        </Tooltip>
      </HStack>
      {loading ? <Skeleton h="40px" borderRadius="md" /> : (
        <VStack align="stretch" spacing={2}>
          <SimpleGrid columns={compact ? 2 : 4} spacing={3}>
            <Stat label="Ground (MSL)" value={fmt(data?.ground_elevation_m)} unit="m" />
            <Stat label="Surveyed (register)" value={fmt(data?.surveyed_elevation_m)} unit="m" />
            {t && <Stat label="Below surroundings" value={fmt(t.depression_m)} unit="m" />}
            {t && <Stat label="Drains toward it" value={t.inflow_fraction == null ? '—' : String(Math.round(t.inflow_fraction * 100))} unit="%" />}
            {p && <Stat label="Profile min / max" value={`${fmt(p.min_m, 1)} / ${fmt(p.max_m, 1)}`} unit="m" />}
            {p && <Stat label="Grade" value={fmt(p.grade_pct)} unit="%" />}
            {p && <Stat label="Low points" value={String(p.low_points?.length ?? 0)} />}
            {p && <Stat label="Length" value={fmt(p.length_m, 0)} unit="m" />}
          </SimpleGrid>
          <Text fontSize="2xs" color="gray.500">
            {data?.ground_elevation_m == null && !p
              ? (error || 'Not fetched yet — needs GPS coordinates and the TessaDEM key on the server.')
              : `Terrain model (trees/buildings removed) · fetched ${data?.fetched_at ? new Date(data.fetched_at).toLocaleString() : '—'}`}
          </Text>
          {error && data?.ground_elevation_m != null && <Text fontSize="2xs" color="red.400">{error}</Text>}
        </VStack>
      )}
    </Box>
  );
};

GroundElevationCard.propTypes = { assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), compact: PropTypes.bool };
export default GroundElevationCard;
